import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui/index.js';
import { useUploads, type UploadSession } from '../features/upload/UploadContext.js';

export function UploadDock() {
  const { sessions, dismiss } = useUploads();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  if (sessions.length === 0) return null;

  const running = sessions.filter((s) => s.status === 'running');
  const errored = sessions.filter((s) => s.status === 'error');
  const completed = sessions.filter((s) => s.status === 'completed');

  const aggregate = aggregateProgress(running);
  const headerLabel =
    running.length > 0
      ? running.length === 1
        ? '1 upload in corso'
        : `${running.length} upload in corso`
      : errored.length > 0
        ? errored.length === 1
          ? 'Upload fallito'
          : `${errored.length} upload falliti`
        : completed.length === 1
          ? 'Upload completato'
          : `${completed.length} upload completati`;

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="glass-vault fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-outline px-4 py-2.5 text-sm font-semibold text-on-surface shadow-xl transition-all hover:border-cyan/40 sm:bottom-6 sm:right-6"
        aria-label={`Mostra dettagli upload — ${headerLabel}`}
      >
        <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-primary text-on-primary">
          <Icon name="cloud_upload" className="text-base" />
          {running.length > 0 ? (
            <span className="absolute -inset-0.5 animate-ping rounded-full bg-cyan/30" />
          ) : null}
        </span>
        <span>{headerLabel}</span>
        {running.length > 0 ? (
          <span className="text-xs text-on-surface-variant">{aggregate.percent}%</span>
        ) : null}
      </button>
    );
  }

  return (
    <div
      role="region"
      aria-label="Stato degli upload"
      className="glass-vault fixed bottom-4 right-4 z-40 flex w-[calc(100vw-2rem)] max-w-sm flex-col rounded-2xl border border-outline shadow-2xl sm:bottom-6 sm:right-6"
    >
      <div className="flex items-center justify-between gap-3 border-b border-outline px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-on-primary">
            <Icon name="cloud_upload" className="text-base" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold text-on-surface">{headerLabel}</span>
            {running.length > 0 ? (
              <span className="text-xs text-on-surface-variant">
                {aggregate.uploaded} / {aggregate.total} foto · {aggregate.percent}%
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:text-cyan"
          aria-label="Riduci a icona"
        >
          <Icon name="expand_more" className="text-lg" />
        </button>
      </div>

      <ul className="flex max-h-72 flex-col gap-2 overflow-auto p-3">
        {sessions.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            onOpen={() => {
              navigate(`/albums/${s.albumId}`);
              setExpanded(false);
            }}
            onDismiss={() => dismiss(s.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({
  session,
  onOpen,
  onDismiss,
}: {
  session: UploadSession;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const total = session.progress?.total ?? session.progress?.files.length ?? 0;
  const uploaded = session.progress?.uploaded ?? 0;
  const percent = computePercent(session);
  const isRunning = session.status === 'running';
  const isError = session.status === 'error';
  const isCompleted = session.status === 'completed';

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-outline bg-surface-container-low p-3">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="flex flex-1 flex-col items-start gap-0.5 text-left"
        >
          <span className="line-clamp-1 text-sm font-medium text-on-surface">
            {session.albumLabel ?? 'Album'}
          </span>
          <span className="text-xs text-on-surface-variant">
            {isError
              ? (session.error ?? 'Errore upload')
              : isCompleted
                ? `${uploaded} foto caricate`
                : `${uploaded} / ${total} foto`}
          </span>
        </button>
        {!isRunning ? (
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:text-cyan"
            aria-label="Chiudi"
          >
            <Icon name="close" className="text-base" />
          </button>
        ) : null}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-lowest">
        <div
          className={`h-full transition-all ${isError ? 'bg-error' : 'bg-cyan'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </li>
  );
}

function aggregateProgress(running: UploadSession[]) {
  let totalBytes = 0;
  let uploadedBytes = 0;
  let total = 0;
  let uploaded = 0;
  for (const s of running) {
    if (!s.progress) continue;
    total += s.progress.total;
    uploaded += s.progress.uploaded;
    for (const f of s.progress.files) {
      totalBytes += f.totalBytes;
      uploadedBytes += f.uploadedBytes;
    }
  }
  const percent = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
  return { total, uploaded, percent };
}

function computePercent(session: UploadSession): number {
  if (session.status === 'completed') return 100;
  if (!session.progress) return 0;
  const totalBytes = session.progress.files.reduce((acc, f) => acc + f.totalBytes, 0);
  const uploadedBytes = session.progress.files.reduce((acc, f) => acc + f.uploadedBytes, 0);
  if (totalBytes === 0) return 0;
  return Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
}
